import React, { useEffect, useState } from "react";
import { AlertTriangle, Download, X } from "lucide-react";
import { ExportScopeType } from "../lib/bidVersioning";

interface Props {
  isOpen: boolean;
  exportVersionCode: string;
  exportVersionName: string;
  hasActiveSubset: boolean;
  currentOnsetDays: number;
  onClose: () => void;
  onConfirm: (scopeType: ExportScopeType, subsetOnsetDays: number) => Promise<void> | void;
}

export function ExportVersionModal({
  isOpen,
  exportVersionCode,
  exportVersionName,
  hasActiveSubset,
  currentOnsetDays,
  onClose,
  onConfirm,
}: Props) {
  const [scopeType, setScopeType] = useState<ExportScopeType>("full");
  const [subsetOnsetDays, setSubsetOnsetDays] = useState("0");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setScopeType(hasActiveSubset ? "subset" : "full");
    setSubsetOnsetDays("0");
    setIsSubmitting(false);
  }, [isOpen, hasActiveSubset]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const numericSubsetOnsetDays = Math.max(0, Number.parseFloat(subsetOnsetDays) || 0);
      await onConfirm(scopeType, scopeType === "subset" ? numericSubsetOnsetDays : currentOnsetDays);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600">
              <Download className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Export Bid Version</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">This export will create a locked historical commercial snapshot.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="mb-1 flex items-center gap-2 font-semibold">
              <AlertTriangle className="h-4 w-4" />
              Exporting will create a locked bid version
            </div>
            <p>
              Exporting this quote will create a new bid version and lock it as a historical commercial snapshot.
              Future commercial changes should happen in a later version.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-600 dark:text-slate-300">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Version name</div>
            <div className="font-semibold text-slate-900 dark:text-slate-100 break-words">{exportVersionName}</div>
          </div>

          {hasActiveSubset ? (
            <div className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Filtered subset detected</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  A filtered subset is currently active. Would you like to export the full project amount, or only the currently filtered subset?
                </p>
              </div>

              <div className="space-y-3">
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                  <input
                    type="radio"
                    name="scopeType"
                    className="mt-1"
                    checked={scopeType === "full"}
                    onChange={() => setScopeType("full")}
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Export full project</div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Snapshot all active bid items for the project.</p>
                  </div>
                </label>

                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                  <input
                    type="radio"
                    name="scopeType"
                    className="mt-1"
                    checked={scopeType === "subset"}
                    onChange={() => setScopeType("subset")}
                  />
                  <div className="w-full">
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Export filtered subset only</div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Snapshot only the currently filtered items into this version.</p>
                    <div className="mt-3 max-w-xs">
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Subset onset days
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={subsetOnsetDays}
                        disabled={scopeType !== "subset"}
                        onChange={(event) => setSubsetOnsetDays(event.target.value)}
                        className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 disabled:bg-slate-50 dark:disabled:bg-slate-800"
                      />
                      <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                        Use this if the subset export needs to include onset days as well.
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-600 dark:text-slate-300">
              This will export the current quote as <strong className="text-slate-900 dark:text-slate-100">{exportVersionName}</strong> and lock it as a historical snapshot.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="rounded-lg bg-[#ffcc01] px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-[#e6b801] disabled:opacity-50"
          >
            {isSubmitting ? "Creating version..." : "Export version"}
          </button>
        </div>
      </div>
    </div>
  );
}
